import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRight, Info } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { MembersUsage, usageService } from '../../../services/UsageService';
import {
  BORDER, DEEP, MemberAvatar, lastActiveLabel, memberName, memberRole, monthLabel, num,
} from './memberUsageUtils';

type SortKey = 'quotes' | 'searches' | 'recent';

const COLS = 'grid-cols-[2.2fr_1.8fr_1fr_0.8fr_0.8fr_0.9fr_1fr_1.3fr_24px]';

/** Everything each advisor did in the selected billing month. */
const MembersUsageSection: React.FC<{ month?: string }> = ({ month }) => {
  const { t, i18n } = useTranslation();
  const locale = i18n.language || 'en';
  const [data, setData] = useState<MembersUsage | null>(null);
  const [error, setError] = useState(false);
  const [sort, setSort] = useState<SortKey>('quotes');

  useEffect(() => {
    if (!month) return;
    let cancelled = false;
    setError(false);
    usageService
      .getMembers(month)
      .then((d) => { if (!cancelled) setData(d); })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [month]);

  const rows = useMemo(() => {
    if (!data) return [];
    const list = [...data.members];
    if (sort === 'searches') list.sort((a, b) => b.searches - a.searches);
    else if (sort === 'recent') list.sort((a, b) => (b.last_active || '').localeCompare(a.last_active || ''));
    else list.sort((a, b) => b.quotes - a.quotes || b.searches - a.searches);
    return list;
  }, [data, sort]);

  if (!month) return null;
  const maxQuotes = Math.max(1, ...rows.map((r) => r.quotes));
  const totalQuotes = data?.totals.quotes || 0;
  const sorts: Array<[SortKey, string]> = [
    ['quotes', t('usage.members.sortQuotes', { defaultValue: 'Most quotes' })],
    ['searches', t('usage.members.sortSearches', { defaultValue: 'Most searches' })],
    ['recent', t('usage.members.sortRecent', { defaultValue: 'Recently active' })],
  ];

  return (
    <section aria-labelledby="members-usage-h" className="relative mt-6 bg-white rounded-2xl overflow-hidden" style={{ border: `1px solid ${BORDER}` }}>
      <div className="flex flex-wrap items-center justify-between gap-4 px-6 py-5" style={{ borderBottom: `1px solid ${BORDER}` }}>
        <div>
          <h2 id="members-usage-h" className="text-lg font-semibold text-gray-900">
            {t('usage.members.byAdvisor', { defaultValue: 'Usage by advisor · {{month}}', month: monthLabel(month, locale) })}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            {data
              ? t('usage.members.byAdvisorHint', {
                  defaultValue: '{{active}} of {{enabled}} enabled advisors active. Select an advisor to see their orders and daily activity.',
                  active: data.totals.active_members,
                  enabled: data.totals.enabled_members,
                })
              : t('usage.loading', { defaultValue: 'Loading…' })}
          </p>
        </div>
        <div role="group" aria-label={t('usage.members.sortBy', { defaultValue: 'Sort advisors by' })} className="flex gap-1 bg-[#f4f2ee] p-1 rounded-lg">
          {sorts.map(([key, label]) => (
            <button
              key={key}
              type="button"
              aria-pressed={sort === key}
              onClick={() => setSort(key)}
              className={`text-[13px] px-3 py-1.5 min-h-[36px] rounded-md border ${sort === key ? 'bg-white border-gray-200 text-gray-900 font-semibold' : 'border-transparent text-gray-600 font-medium'}`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {error && (
        <div className="px-6 py-4 text-sm text-red-800 bg-red-50">
          {t('usage.members.loadError', { defaultValue: 'Could not load advisor usage.' })}
        </div>
      )}

      {data && (
        <div className="overflow-x-auto">
          <div className="min-w-[960px]" role="table" aria-label={t('usage.members.tableAria', { defaultValue: 'Usage by advisor' })}>
            <div role="row" className={`grid ${COLS} gap-3 px-6 py-3 text-xs uppercase tracking-wider text-gray-500 bg-[#faf9f6]`} style={{ borderBottom: `1px solid ${BORDER}` }}>
              <div role="columnheader">{t('usage.members.advisor', { defaultValue: 'Advisor' })}</div>
              <div role="columnheader">{t('usage.members.quotesShare', { defaultValue: 'Quotes · share' })}</div>
              <div role="columnheader" className="text-right">{t('usage.metrics.part_queries', { defaultValue: 'Part searches' })}</div>
              <div role="columnheader" className="text-right">{t('usage.metrics.images', { defaultValue: 'Images' })}</div>
              <div role="columnheader" className="text-right">{t('usage.metrics.pdfs', { defaultValue: 'PDFs' })}</div>
              <div role="columnheader" className="text-right">{t('usage.metrics.orders', { defaultValue: 'Orders' })}</div>
              <div role="columnheader" className="text-right">{t('usage.members.partsFound', { defaultValue: 'Parts found' })}</div>
              <div role="columnheader">{t('usage.members.lastActive', { defaultValue: 'Last active' })}</div>
              <div role="columnheader"><span className="sr-only">{t('usage.members.open', { defaultValue: 'Open' })}</span></div>
            </div>

            {rows.map((m) => {
              const idle = !m.quotes && !m.searches && !m.orders;
              const share = totalQuotes ? Math.round((m.quotes / totalQuotes) * 100) : 0;
              return (
                <Link
                  key={m.key}
                  role="row"
                  to={`/usage/members/${encodeURIComponent(m.key)}?month=${month}`}
                  className={`grid ${COLS} gap-3 items-center px-6 py-3.5 min-h-[44px] text-gray-900 hover:bg-[#faf9f6] no-underline`}
                  style={{ borderBottom: '1px solid #f1eee8' }}
                  aria-label={t('usage.members.rowAria', {
                    defaultValue: '{{name}}: {{quotes}} quotes, {{searches}} part searches. Open details.',
                    name: memberName(m.key, m.name, t), quotes: m.quotes, searches: m.searches,
                  })}
                >
                  <div role="cell" className="flex items-center gap-3 min-w-0">
                    <MemberAvatar name={m.name} kind={m.kind} />
                    <div className="min-w-0">
                      <div className="text-[15px] font-semibold truncate">{memberName(m.key, m.name, t)}</div>
                      <div className="flex flex-wrap gap-1.5 mt-0.5">
                        <span className="text-xs text-gray-500">{memberRole(m.role, t)}</span>
                        {m.disabled && (
                          <span className="text-[11px] font-semibold text-gray-600 bg-[#f1efea] px-2 rounded-full">
                            {t('usage.members.disabledBadge', { defaultValue: 'Disabled' })}
                          </span>
                        )}
                        {idle && !m.disabled && (
                          <span className="text-[11px] font-semibold text-[#8a4b0c] bg-[#fdf0e1] px-2 rounded-full">
                            {t('usage.members.noActivity', { defaultValue: 'No activity this month' })}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <div role="cell" className="flex items-center gap-2.5">
                    <span className="text-[15px] font-semibold w-7 text-right tabular-nums">{num(m.quotes)}</span>
                    <div className="flex-1 h-2 bg-[#eeebe4] rounded overflow-hidden">
                      <div className="h-full rounded" style={{ background: DEEP, width: `${Math.round((m.quotes / maxQuotes) * 100)}%` }} />
                    </div>
                    <span className="text-xs text-gray-500 w-9 text-right tabular-nums">{share}%</span>
                  </div>
                  <div role="cell" className="text-right tabular-nums">{num(m.searches)}</div>
                  <div role="cell" className="text-right tabular-nums">{num(m.images)}</div>
                  <div role="cell" className="text-right tabular-nums">{num(m.pdfs)}</div>
                  <div role="cell" className="text-right tabular-nums">{num(m.orders)}</div>
                  <div role="cell" className="text-right tabular-nums">{m.parts_found_pct == null ? '—' : `${m.parts_found_pct}%`}</div>
                  <div role="cell" className="text-sm text-gray-700">{lastActiveLabel(m.last_active, locale, t)}</div>
                  <div role="cell" className="text-gray-400 flex justify-end"><ChevronRight className="w-4 h-4" aria-hidden="true" /></div>
                </Link>
              );
            })}

            <div role="row" className={`grid ${COLS} gap-3 items-center px-6 py-3.5 bg-[#faf9f6] font-semibold tabular-nums`}>
              <div role="rowheader" className="text-sm">{t('usage.members.orgTotal', { defaultValue: 'Organization total' })}</div>
              <div role="cell">{num(data.totals.quotes)}</div>
              <div role="cell" className="text-right">{num(data.totals.searches)}</div>
              <div role="cell" className="text-right">{num(data.totals.images)}</div>
              <div role="cell" className="text-right">{num(data.totals.pdfs)}</div>
              <div role="cell" className="text-right">{num(data.totals.orders)}</div>
              <div role="cell" className="text-right">{data.totals.parts_found_pct == null ? '—' : `${data.totals.parts_found_pct}%`}</div>
              <div role="cell" />
              <div role="cell" />
            </div>
          </div>
        </div>
      )}

      <div className="flex gap-2 items-start px-6 py-3.5 text-[13px] text-gray-500" style={{ borderTop: `1px solid ${BORDER}` }}>
        <Info className="w-4 h-4 flex-shrink-0 mt-0.5" aria-hidden="true" />
        <span>
          {t('usage.members.footnote', {
            defaultValue: 'Quotes and searches are credited to the advisor who ran the chat, in the month it started. Email and WhatsApp orders are grouped together. Cata Support appears when our team works an order for you. Disabled users are hidden here; turn them on in Quotes per month.',
          })}
        </span>
      </div>
    </section>
  );
};

export default MembersUsageSection;
